import React, { useEffect, useState } from "react";
import { Flame, Gamepad2, Wand2, Heart, Check, Gift, Loader2, Trophy, CalendarClock, Swords, Crown } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const ICONS = { gamepad: Gamepad2, wand: Wand2, heart: Heart };

export default function DailyQuests() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [lb, setLb] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => {
    api.get("/quests/today").then((r) => setData(r.data)).catch(() => setData(null));
    api.get("/quests/week/leaderboard").then((r) => setLb(r.data)).catch(() => setLb(null));
  };
  useEffect(() => { load(); }, []);

  const claim = async (q) => {
    setBusy(q.id);
    try {
      const { data: res } = await api.post("/quests/claim", { quest_id: q.id });
      toast.success(`+${res.reward} credits claimed! 🎁`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't claim reward"); }
    finally { setBusy(""); }
  };

  const claimWeekly = async () => {
    setBusy("weekly");
    try {
      const { data: res } = await api.post("/quests/week/claim");
      toast.success(`Weekly challenge complete! +${res.reward} credits 🏆`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't claim reward"); }
    finally { setBusy(""); }
  };

  const claimStreak = async (days, reward) => {
    setBusy(`sr${days}`);
    try {
      const { data: res } = await api.post("/quests/streak/claim", { days });
      toast.success(`${res.badge} ${res.title} unlocked! +${res.reward} credits`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't claim reward"); }
    finally { setBusy(""); }
  };

  const setRival = async (id, name) => {
    setBusy(`rival${id}`);
    try {
      await api.post("/quests/rival", { rival_id: id });
      toast.success(`⚔️ ${name} is now your rival!`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't set rival"); }
    finally { setBusy(""); }
  };

  const callout = async () => {
    setBusy("callout");
    try {
      await api.post("/quests/rival/callout");
      toast.success("🔥 Callout sent — game on!");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't send callout"); }
    finally { setBusy(""); }
  };

  if (!data) return null;
  const done = data.quests.filter((q) => q.claimed).length;
  const w = data.weekly;
  const sr = data.streak_rewards || [];
  const rv = data.rival;

  return (
    <div className="mb-8 rounded-2xl border border-[hsl(var(--border))] bg-gradient-to-br from-amber-500/10 via-[hsl(var(--card))] to-[hsl(var(--card))] p-5" data-testid="daily-quests">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl grid place-items-center bg-gradient-to-br from-amber-400 to-orange-500 shrink-0 shadow-lg shadow-orange-500/20">
            <Flame className="w-6 h-6 text-white" />
          </div>
          <div>
            <div className="font-display font-extrabold text-lg leading-tight" data-testid="quest-streak">
              {data.streak}-day streak 🔥
            </div>
            <div className="text-xs text-[hsl(var(--muted-foreground))]">
              {data.streak > 1 ? "Keep it alive — play something today!" : "Play daily to build your streak."}
              {data.longest_streak > 1 ? ` Best: ${data.longest_streak} days.` : ""}
            </div>
          </div>
        </div>
        <div className="text-xs font-semibold text-[hsl(var(--muted-foreground))]" data-testid="quest-summary">
          Daily quests · {done}/{data.quests.length} done
        </div>
      </div>

      {/* Streak Rewards — milestone bonuses */}
      {sr.length > 0 && (
        <div className="mb-4 rounded-xl border border-orange-500/25 bg-gradient-to-br from-orange-500/10 to-transparent p-3" data-testid="streak-rewards">
          <div className="text-[10px] font-bold uppercase tracking-wide text-orange-400 mb-2 flex items-center gap-1.5"><Flame className="w-3.5 h-3.5" /> Streak Rewards</div>
          <div className="flex flex-wrap gap-2">
            {sr.map((m) => (
              <div key={m.days} data-testid={`streak-reward-${m.days}`}
                className={`rounded-lg border px-2.5 py-1.5 flex items-center gap-2 ${m.claimed ? "border-emerald-500/40 bg-emerald-500/5" : m.claimable ? "border-orange-500/50 bg-orange-500/10" : m.reached ? "border-[hsl(var(--border))]" : "border-[hsl(var(--border))] opacity-55"}`}>
                <span className="text-lg leading-none">{m.badge}</span>
                <div className="leading-tight">
                  <div className="text-xs font-bold">{m.days}-day</div>
                  <div className="text-[10px] text-[hsl(var(--muted-foreground))]">+{m.reward} · {m.title}</div>
                </div>
                {m.claimed ? (
                  <Check className="w-4 h-4 text-emerald-500 shrink-0" data-testid={`streak-claimed-${m.days}`} />
                ) : m.claimable ? (
                  <Button size="sm" onClick={() => claimStreak(m.days, m.reward)} disabled={busy === `sr${m.days}`}
                    className="h-6 rounded-full text-[10px] px-2 gap-1 bg-gradient-to-r from-orange-400 to-red-500 text-white" data-testid={`streak-claim-${m.days}`}>
                    {busy === `sr${m.days}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Gift className="w-3 h-3" />} Claim
                  </Button>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Rivalry — head-to-head + callout */}
      {rv && (
        <div className="mb-4 rounded-xl border border-rose-500/30 bg-gradient-to-br from-rose-500/10 to-transparent p-3.5" data-testid="rival-card">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="text-[10px] font-bold uppercase tracking-wide text-rose-400 flex items-center gap-1.5"><Swords className="w-3.5 h-3.5" /> Rivalry</div>
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-lg">🧑‍🚀</span>
                <div className="leading-tight"><div className="text-xs font-bold">You</div><div className="text-[10px] text-[hsl(var(--muted-foreground))]">🔥 {rv.me.streak}d · {rv.me.xp} XP</div></div>
              </div>
              <div className={`text-sm font-extrabold ${rv.you_lead_streak ? "text-emerald-400" : "text-rose-400"}`}>VS</div>
              <div className="flex items-center gap-1.5">
                <span className="text-lg">{rv.rival.avatar}</span>
                <div className="leading-tight"><div className="text-xs font-bold truncate max-w-[110px]">{rv.rival.name}</div><div className="text-[10px] text-[hsl(var(--muted-foreground))]">🔥 {rv.rival.streak}d · {rv.rival.xp} XP</div></div>
              </div>
            </div>
            <Button size="sm" onClick={callout} disabled={busy === "callout" || !rv.can_callout} data-testid="rival-callout-btn"
              className="h-7 rounded-full text-[11px] px-3 gap-1 bg-gradient-to-r from-rose-500 to-orange-500 text-white">
              {busy === "callout" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Swords className="w-3.5 h-3.5" />}
              {rv.can_callout ? "Call out" : "Called out ✓"}
            </Button>
          </div>
          <div className="text-[11px] mt-2 flex items-center gap-1.5">
            {rv.you_lead_streak
              ? <span className="text-emerald-400 font-semibold flex items-center gap-1"><Crown className="w-3 h-3" /> You lead the streak — keep it up!</span>
              : <span className="text-rose-400 font-semibold">You're {rv.rival.streak - rv.me.streak} day(s) behind — don't break the chain!</span>}
          </div>
        </div>
      )}
      {/* Pick a rival from this week's finishers */}
      {!rv && lb?.leaders?.some((l) => l.user_id !== user?.id) && (
        <div className="mb-4 rounded-xl border border-rose-500/25 bg-rose-500/5 p-3" data-testid="rival-pick">
          <div className="text-[10px] font-bold uppercase tracking-wide text-rose-400 mb-2 flex items-center gap-1.5"><Swords className="w-3.5 h-3.5" /> Start a rivalry</div>
          <div className="flex flex-wrap gap-2">
            {lb.leaders.filter((l) => l.user_id !== user?.id).slice(0, 4).map((l) => (
              <button key={l.user_id} onClick={() => setRival(l.user_id, l.name)} disabled={busy === `rival${l.user_id}`}
                data-testid={`rival-pick-${l.user_id}`}
                className="rounded-full border border-rose-500/40 bg-[hsl(var(--card))] px-2.5 py-1 text-xs flex items-center gap-1.5 hover:bg-rose-500/10 transition-colors">
                {busy === `rival${l.user_id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <span>{l.avatar}</span>} {l.name}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-3 gap-3">
        {data.quests.map((q) => {
          const Icon = ICONS[q.icon] || Gift;
          const pct = Math.round((q.progress / q.target) * 100);
          return (
            <div key={q.id} className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3 flex flex-col gap-2" data-testid={`quest-item-${q.id}`}>
              <div className="flex items-center gap-2">
                <Icon className="w-4 h-4 text-amber-500 shrink-0" />
                <div className="text-sm font-semibold leading-tight">{q.title}</div>
              </div>
              <div className="text-xs text-[hsl(var(--muted-foreground))] leading-snug">{q.desc}</div>
              <div className="h-1.5 rounded-full bg-[hsl(var(--muted))] overflow-hidden">
                <div className="h-full bg-gradient-to-r from-amber-400 to-orange-500 transition-all duration-500" style={{ width: `${pct}%` }} data-testid={`quest-progress-${q.id}`} />
              </div>
              <div className="flex items-center justify-between mt-auto pt-0.5">
                <span className="text-[11px] text-[hsl(var(--muted-foreground))] tabular-nums">{q.progress}/{q.target}</span>
                {q.claimed ? (
                  <span className="text-[11px] font-semibold text-emerald-500 flex items-center gap-1" data-testid={`quest-claimed-${q.id}`}>
                    <Check className="w-3.5 h-3.5" /> +{q.reward}
                  </span>
                ) : q.claimable ? (
                  <Button size="sm" onClick={() => claim(q)} disabled={busy === q.id}
                    className="h-7 rounded-full gap-1 bg-gradient-to-r from-amber-400 to-orange-500 text-black text-[11px] px-3 hover:opacity-90"
                    data-testid={`quest-claim-${q.id}`}>
                    {busy === q.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Gift className="w-3 h-3" />} Claim +{q.reward}
                  </Button>
                ) : (
                  <span className="text-[11px] text-[hsl(var(--muted-foreground))]">+{q.reward} credits</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {w && (
        <div className="mt-4 rounded-xl border border-indigo-500/30 bg-gradient-to-br from-indigo-500/10 to-transparent p-4" data-testid="weekly-challenge">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="w-10 h-10 rounded-xl grid place-items-center bg-gradient-to-br from-indigo-500 to-violet-500 shrink-0 shadow-lg shadow-indigo-500/20">
              <Trophy className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] font-bold uppercase tracking-wide text-indigo-400">Weekly Challenge</span>
                <span className="text-[11px] text-[hsl(var(--muted-foreground))] flex items-center gap-1"><CalendarClock className="w-3 h-3" /> {w.days_left} day{w.days_left === 1 ? "" : "s"} left</span>
              </div>
              <div className="font-display font-bold leading-tight" data-testid="weekly-title">{w.title}</div>
              <div className="text-xs text-[hsl(var(--muted-foreground))]">{w.desc}</div>
            </div>
            <div className="shrink-0">
              {w.claimed ? (
                <span className="text-xs font-semibold text-emerald-500 flex items-center gap-1" data-testid="weekly-claimed"><Check className="w-4 h-4" /> +{w.reward} claimed</span>
              ) : w.claimable ? (
                <Button size="sm" onClick={claimWeekly} disabled={busy === "weekly"}
                  className="rounded-full gap-1 bg-gradient-to-r from-indigo-500 to-violet-500 text-white hover:opacity-90" data-testid="weekly-claim">
                  {busy === "weekly" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />} Claim +{w.reward}
                </Button>
              ) : (
                <span className="text-xs text-[hsl(var(--muted-foreground))]">Reward: <b className="text-indigo-400">+{w.reward}</b></span>
              )}
            </div>
          </div>
          <div className="mt-3 h-2 rounded-full bg-[hsl(var(--muted))] overflow-hidden">
            <div className="h-full bg-gradient-to-r from-indigo-500 to-violet-500 transition-all duration-500" style={{ width: `${Math.round((w.progress / w.target) * 100)}%` }} data-testid="weekly-progress" />
          </div>
          <div className="mt-1 flex items-center gap-2 flex-wrap text-[11px] text-[hsl(var(--muted-foreground))]">
            <span className="tabular-nums">{w.progress}/{w.target}</span>
            {lb && lb.total_completed > 0 && (
              <span className="flex items-center gap-1.5 flex-wrap" data-testid="weekly-leaderboard">
                <span className="text-[hsl(var(--border))]">·</span>
                <Trophy className="w-3 h-3 text-indigo-400" />
                <b className="text-[hsl(var(--foreground))]">{lb.total_completed}</b> finished this week
                {lb.leaders.slice(0, 3).map((l, i) => (
                  <span key={l.user_id}>{["🥇", "🥈", "🥉"][i]} {l.name}</span>
                ))}
                {lb.my_rank && <span className="font-semibold text-indigo-400">· You're #{lb.my_rank} 🏆</span>}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
